import React, { useEffect, useState, useCallback } from 'react';
import {
  Row, Col, Button, Modal, Form,
  Badge, Alert, Spinner, InputGroup,
} from 'react-bootstrap';
import {
  FaPlus, FaSyncAlt, FaEdit, FaTrash,
  FaMobileAlt, FaTabletAlt, FaLaptop, FaDesktop,
  FaPhone, FaPrint, FaBarcode, FaCog,
  FaHistory, FaUserCheck, FaUserTimes,
  FaSearch, FaFileExcel, FaUpload, FaFilter, FaFilePdf,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { generateBonRemisePDF } from '../utils/parcRemisePDF';

// ── Constantes ────────────────────────────────────────────────────
const MAIN   = '#6d4e36';
const TYPES  = ['Téléphone', 'Tablette', 'Laptop', 'PC fixe', 'Téléphone fixe', 'Imprimante', 'Scanner', 'Autre'];
const ETATS  = ['Neuf', 'Bon état', 'Usagé', 'En panne', 'Perdu/Volé', 'Réformé'];
const STATUTS = ['Disponible', 'Affecté', 'En réparation', 'Hors service'];

const TYPE_ICONS = {
  'Téléphone':      <FaMobileAlt />,
  'Tablette':       <FaTabletAlt />,
  'Laptop':         <FaLaptop />,
  'PC fixe':        <FaDesktop />,
  'Téléphone fixe': <FaPhone />,
  'Imprimante':     <FaPrint />,
  'Scanner':        <FaBarcode />,
  'Autre':          <FaCog />,
};

const STATUT_COLORS = {
  'Disponible':    '#2e7d32',
  'Affecté':       '#1565c0',
  'En réparation': '#e67e22',
  'Hors service':  '#6c757d',
};

const ETAT_COLORS = {
  'Neuf':        '#2e7d32',
  'Bon état':    '#1565c0',
  'Usagé':       '#e67e22',
  'En panne':    '#c0392b',
  'Perdu/Volé':  '#6f42c1',
  'Réformé':     '#6c757d',
};

const SOC_COLORS = [
  { name: 'pistas',      color: '#1565c0' },
  { name: 'rutas',       color: '#2e7d32' },
  { name: 'sepgb',       color: '#6f42c1' },
  { name: 'elite parcs', color: '#0d9488' },
  { name: 'cadex',       color: '#e67e22' },
  { name: 'brostol',     color: '#c0392b' },
  { name: 'siege',       color: '#6d4e36' },
];

const socColor = (n) =>
  SOC_COLORS.find(s => s.name === (n || '').toLowerCase())?.color || '#6d4e36';

// ── Micro-composants ──────────────────────────────────────────────
const StatutBadge = ({ statut }) => (
  <span style={{
    background: STATUT_COLORS[statut] || '#6c757d',
    color: '#fff', borderRadius: 6, padding: '2px 9px',
    fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
  }}>{statut || '—'}</span>
);

const EtatBadge = ({ etat }) => (
  <span style={{
    background: (ETAT_COLORS[etat] || '#ccc') + '22',
    color: ETAT_COLORS[etat] || '#333',
    border: `1px solid ${ETAT_COLORS[etat] || '#ccc'}`,
    borderRadius: 6, padding: '2px 8px',
    fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap',
  }}>{etat || '—'}</span>
);

const SocBadge = ({ societe }) => (
  <span style={{
    background: socColor(societe) + '18',
    color: socColor(societe),
    border: `1px solid ${socColor(societe)}33`,
    borderRadius: 6, padding: '2px 8px',
    fontSize: '0.78rem', fontWeight: 700,
    textTransform: 'uppercase', whiteSpace: 'nowrap',
  }}>{societe || '—'}</span>
);

const KpiCard = ({ label, value, color, active, onClick }) => (
  <div onClick={onClick} style={{
    background: active ? color : '#fff',
    border: `2px solid ${color}`,
    borderRadius: 12, padding: '12px 18px',
    cursor: 'pointer', transition: 'all .18s',
    minWidth: 110, textAlign: 'center',
    boxShadow: active ? `0 2px 12px ${color}55` : '0 1px 4px #00000015',
    userSelect: 'none',
  }}>
    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: active ? '#fff' : color }}>{value}</div>
    <div style={{ fontSize: '0.72rem', color: active ? '#ffffffcc' : '#555', fontWeight: 600, marginTop: 2 }}>{label}</div>
  </div>
);

const TypeChip = ({ icon, label, count, active, onClick }) => (
  <span onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: active ? MAIN : '#f5ede8',
    color: active ? '#fff' : MAIN,
    border: `1.5px solid ${active ? MAIN : '#d8c8b8'}`,
    borderRadius: 20, padding: '3px 10px',
    fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer', margin: '2px 3px', transition: 'all .15s', userSelect: 'none',
  }}>
    {icon}&nbsp;{label}
    <span style={{
      background: active ? '#ffffff44' : MAIN + '22',
      color: active ? '#fff' : MAIN,
      borderRadius: 10, padding: '0 5px', fontSize: '0.7rem', fontWeight: 700,
    }}>{count}</span>
  </span>
);

// ── CSS ───────────────────────────────────────────────────────────
const CSS = `
.pit { border-collapse: separate; border-spacing: 0; width: 100%; }
.pit-th {
  background: linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%);
  color: #fff; font-size: .78rem; font-weight: 700;
  padding: 10px 8px; white-space: nowrap;
  position: sticky; top: 0; z-index: 1; cursor: pointer; user-select: none;
}
.pit-th:first-child { border-radius: 8px 0 0 0; }
.pit-th:last-child  { border-radius: 0 8px 0 0; }
.pit-th:hover { background: linear-gradient(135deg, #8b5e3c 0%, #a07050 100%); }
.pit-tr:nth-child(even) td { background: #fdfaf8; }
.pit-tr:hover td { background: #f5ede8; box-shadow: inset 3px 0 0 #6d4e36; }
.pit-td { padding: 8px 8px; font-size: .82rem; border-bottom: 1px solid #ede0d4; vertical-align: middle; }
.pit-btn { border: none; background: none; padding: 4px 7px; border-radius: 6px; cursor: pointer; transition: background .15s; }
.pit-btn:hover { background: #f0e0d0; }
.pit-inp { border: 1.5px solid #d8c8b8 !important; border-radius: 8px !important; font-size: .87rem; }
.pit-inp:focus { border-color: #6d4e36 !important; box-shadow: 0 0 0 2px #6d4e3633 !important; }
`;

// ── Formulaires vides ─────────────────────────────────────────────
const EMPTY = {
  type_appareil: '', marque: '', modele: '', numero_serie: '', imei: '',
  etat: '', statut: 'Disponible', societe: '', affecte_a: '',
  date_affectation: '', date_retour_prevu: '', telephone_id: '',
  prix_achat: '', fournisseur: '', date_achat: '', observation: '',
};
const EMPTY_AFFECT = { affecte_a: '', date_affectation: '', date_retour_prevu: '', accessoires: '', note: '' };
const EMPTY_RESTIT = { date_retour: '', motif_retour: '', note: '', nouveau_statut: 'Disponible' };

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtPrice = (p) => p != null ? `${Number(p).toLocaleString('fr-FR')} DH` : '—';

// ── Page ──────────────────────────────────────────────────────────
export default function ParcInformatiquePage({ user }) {
  const canWrite = user?.role === 'admin' ||
    (user?.privileges || []).includes('parc-informatique:write');

  const [rows, setRows]       = useState([]);
  const [societes, setSocietes] = useState([]);
  const [telephones, setTelephones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // filtres
  const [search, setSearch]     = useState('');
  const [fType, setFType]       = useState('');
  const [fStatut, setFStatut]   = useState('');
  const [fEtat, setFEtat]       = useState('');
  const [fSociete, setFSociete] = useState('');
  const [kpi, setKpi]           = useState('');
  const [sortKey, setSortKey]   = useState('type_appareil');
  const [sortAsc, setSortAsc]   = useState(true);

  // CRUD
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [formErr, setFormErr]     = useState('');
  const [delTarget, setDelTarget] = useState(null);

  // Historique
  const [showHist, setShowHist]       = useState(false);
  const [histRows, setHistRows]       = useState([]);
  const [histAppareil, setHistAppareil] = useState(null);
  const [loadHist, setLoadHist]       = useState(false);

  // Affecter
  const [showAff, setShowAff]   = useState(false);
  const [affTarget, setAffTarget] = useState(null);
  const [affForm, setAffForm]   = useState(EMPTY_AFFECT);
  const [affSaving, setAffSaving] = useState(false);
  const [affErr, setAffErr]     = useState('');

  // Restituer
  const [showRest, setShowRest]   = useState(false);
  const [restTarget, setRestTarget] = useState(null);
  const [restForm, setRestForm]   = useState(EMPTY_RESTIT);
  const [restSaving, setRestSaving] = useState(false);
  const [restErr, setRestErr]     = useState('');

  const [importing, setImporting] = useState(false);

  // Bon de remise rapide (sans affectation)
  const [showQuick, setShowQuick] = useState(false);
  const [quickForm, setQuickForm] = useState({ parc_id: '', affecte_a: '', societe: '' });
  const [quickErr, setQuickErr]   = useState('');

  // ── chargement ────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [dataRes, socRes, telRes] = await Promise.all([
        axios.get('/parc-informatique'),
        axios.get('/parc-informatique/societes'),
        axios.get('/telephones').catch(() => ({ data: [] })),
      ]);
      setRows(dataRes.data);
      setSocietes(socRes.data);
      setTelephones(telRes.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── KPIs ─────────────────────────────────────────────────────
  const byStatut = STATUTS.reduce((a, s) => ({ ...a, [s]: rows.filter(r => r.statut === s).length }), {});
  const byType   = TYPES.reduce((a, t)   => ({ ...a, [t]: rows.filter(r => r.type_appareil === t).length }), {});

  // ── filtrage + tri ───────────────────────────────────────────
  const filtered = rows
    .filter(r => !kpi     || r.statut       === kpi)
    .filter(r => !fType   || r.type_appareil === fType)
    .filter(r => !fStatut || r.statut        === fStatut)
    .filter(r => !fEtat   || r.etat          === fEtat)
    .filter(r => !fSociete|| r.societe        === fSociete)
    .filter(r => {
      if (!search) return true;
      const q = search.toLowerCase();
      return [r.marque, r.modele, r.numero_serie, r.imei, r.affecte_a, r.fournisseur]
        .some(v => v && v.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      const va = (a[sortKey] || '').toString().toLowerCase();
      const vb = (b[sortKey] || '').toString().toLowerCase();
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const toggleSort = (k) => {
    if (sortKey === k) setSortAsc(v => !v);
    else { setSortKey(k); setSortAsc(true); }
  };

  // ── CRUD ─────────────────────────────────────────────────────
  const openCreate = () => {
    setEditRow(null); setForm(EMPTY); setFormErr(''); setShowModal(true);
  };

  const openEdit = (r) => {
    setEditRow(r);
    setForm({
      type_appareil:    r.type_appareil    || '',
      marque:           r.marque           || '',
      modele:           r.modele           || '',
      numero_serie:     r.numero_serie     || '',
      imei:             r.imei             || '',
      etat:             r.etat             || '',
      statut:           r.statut           || 'Disponible',
      societe:          r.societe          || '',
      affecte_a:        r.affecte_a        || '',
      date_affectation: r.date_affectation || '',
      date_retour_prevu:r.date_retour_prevu|| '',
      telephone_id:     r.telephone_id     != null ? String(r.telephone_id) : '',
      prix_achat:       r.prix_achat       != null ? String(r.prix_achat) : '',
      fournisseur:      r.fournisseur      || '',
      date_achat:       r.date_achat       || '',
      observation:      r.observation      || '',
    });
    setFormErr(''); setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true); setFormErr('');
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.prix_achat != null) {
        const p = parseFloat(String(payload.prix_achat).replace(',', '.'));
        payload.prix_achat = isNaN(p) ? null : p;
      }
      if (payload.telephone_id != null) {
        const t = parseInt(payload.telephone_id);
        payload.telephone_id = isNaN(t) ? null : t;
      }
      if (editRow) {
        await axios.put(`/parc-informatique/${editRow.id}`, payload);
        setSuccess('Appareil modifié ✓');
      } else {
        await axios.post('/parc-informatique', payload);
        setSuccess('Appareil ajouté ✓');
      }
      setShowModal(false); load();
    } catch (e) {
      setFormErr(e?.response?.data?.message || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!delTarget) return;
    try {
      await axios.delete(`/parc-informatique/${delTarget.id}`);
      setSuccess('Appareil supprimé ✓');
      setDelTarget(null); load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur suppression');
      setDelTarget(null);
    }
  };

  // ── Historique ───────────────────────────────────────────────
  const openHist = async (r) => {
    setHistAppareil(r); setShowHist(true); setLoadHist(true);
    try {
      const res = await axios.get(`/parc-informatique/${r.id}/historique`);
      setHistRows(res.data);
    } catch { setHistRows([]); }
    finally { setLoadHist(false); }
  };

  // ── Affecter ─────────────────────────────────────────────────
  const openAff = (r) => {
    setAffTarget(r);
    setAffForm({ ...EMPTY_AFFECT, date_affectation: new Date().toISOString().slice(0, 10) });
    setAffErr(''); setShowAff(true);
  };

  const handleAff = async () => {
    if (!affForm.affecte_a.trim()) { setAffErr('Le nom est requis.'); return; }
    setAffSaving(true);
    try {
      const combinedNote = [
        affForm.accessoires.trim() ? `Accessoires : ${affForm.accessoires.trim()}` : null,
        affForm.note.trim() || null,
      ].filter(Boolean).join('\n');

      await axios.post(`/parc-informatique/${affTarget.id}/affecter`, {
        affecte_a:         affForm.affecte_a,
        date_affectation:  affForm.date_affectation,
        date_retour_prevu: affForm.date_retour_prevu,
        note:              combinedNote || null,
      });

      generateBonRemisePDF({
        appareil:          affTarget,
        affecte_a:         affForm.affecte_a,
        date_affectation:  affForm.date_affectation,
        date_retour_prevu: affForm.date_retour_prevu,
        note:              combinedNote,
        userName:          user?.name,
      });

      setSuccess(`Affecté à ${affForm.affecte_a} ✓ — bon de remise téléchargé`);
      setShowAff(false); load();
    } catch (e) {
      setAffErr(e?.response?.data?.message || 'Erreur');
    } finally { setAffSaving(false); }
  };

  const openBonRemise = async (r) => {
    try {
      const res = await axios.get(`/parc-informatique/${r.id}/historique`);
      const open = (res.data || []).find(h => !h.date_retour) || res.data?.[0];
      generateBonRemisePDF({
        appareil:          r,
        affecte_a:         r.affecte_a,
        date_affectation:  r.date_affectation,
        date_retour_prevu: r.date_retour_prevu,
        note:              open?.note || '',
        userName:          user?.name,
      });
    } catch (e) {
      setError('Impossible de générer le bon de remise.');
    }
  };

  // ── Bon de remise rapide (article + bénéficiaire + société, sans affectation) ──
  const openQuick = () => {
    setQuickForm({ parc_id: '', affecte_a: '', societe: '' });
    setQuickErr(''); setShowQuick(true);
  };

  const handleQuick = () => {
    if (!quickForm.parc_id) { setQuickErr('Choisissez un article.'); return; }
    const appareil = rows.find(r => r.id === Number(quickForm.parc_id));
    if (!appareil) { setQuickErr('Article introuvable.'); return; }

    generateBonRemisePDF({
      appareil,
      affecte_a: quickForm.affecte_a,
      societe:   quickForm.societe,
    });
    setShowQuick(false);
  };

  // ── Restituer ────────────────────────────────────────────────
  const openRest = (r) => {
    setRestTarget(r);
    setRestForm({ ...EMPTY_RESTIT, date_retour: new Date().toISOString().slice(0, 10) });
    setRestErr(''); setShowRest(true);
  };

  const handleRest = async () => {
    setRestSaving(true);
    try {
      await axios.post(`/parc-informatique/${restTarget.id}/restituer`, restForm);
      setSuccess('Restitution enregistrée ✓');
      setShowRest(false); load();
    } catch (e) {
      setRestErr(e?.response?.data?.message || 'Erreur');
    } finally { setRestSaving(false); }
  };

  // ── Import Excel ─────────────────────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const mapped = json.map(r => ({
        type_appareil:    r['Type']            || r['type_appareil']    || '',
        marque:           r['Marque']           || r['marque']           || '',
        modele:           r['Modèle']           || r['modele']           || '',
        numero_serie:     r['N° Série']         || r['numero_serie']     || '',
        imei:             r['IMEI']             || r['imei']             || '',
        etat:             r['État']             || r['etat']             || '',
        statut:           r['Statut']           || r['statut']           || 'Disponible',
        societe:          r['Société']          || r['societe']          || '',
        affecte_a:        r['Affecté à']        || r['affecte_a']        || '',
        date_affectation: r['Date affectation'] || r['date_affectation'] || '',
        prix_achat:       r['Prix achat']       || r['prix_achat']       || '',
        fournisseur:      r['Fournisseur']      || r['fournisseur']      || '',
        date_achat:       r['Date achat']       || r['date_achat']       || '',
        observation:      r['Observation']      || r['observation']      || '',
      }));
      const res = await axios.post('/parc-informatique/import', { rows: mapped });
      setSuccess(`Import OK : ${res.data.created} créés, ${res.data.skipped} ignorés.`);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur import');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // ── Export Excel ─────────────────────────────────────────────
  const handleExport = () => {
    const data = filtered.map(r => ({
      'Type':               r.type_appareil    || '',
      'Marque':             r.marque           || '',
      'Modèle':             r.modele           || '',
      'N° Série':           r.numero_serie     || '',
      'IMEI':               r.imei             || '',
      'État':               r.etat             || '',
      'Statut':             r.statut           || '',
      'Société':            r.societe          || '',
      'Affecté à':          r.affecte_a        || '',
      'Date affectation':   r.date_affectation || '',
      'Date retour prévu':  r.date_retour_prevu|| '',
      'Ligne tél':          r.ligne?.tel        || '',
      'Prix achat (DH)':    r.prix_achat       != null ? r.prix_achat : '',
      'Fournisseur':        r.fournisseur      || '',
      'Date achat':         r.date_achat       || '',
      'Observation':        r.observation      || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Array(16).fill({ wch: 18 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Parc IT');
    XLSX.writeFile(wb, `parc_informatique_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const Th = ({ k, label }) => (
    <th className="pit-th" onClick={() => toggleSort(k)}>
      {label} {sortKey === k ? (sortAsc ? '▲' : '▼') : ''}
    </th>
  );

  // ── Rendu ─────────────────────────────────────────────────────
  return (
    <>
      <style>{CSS}</style>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem 1.25rem 4rem' }}>

        {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 style={{ color: MAIN, fontWeight: 800, margin: 0 }}>💻 Parc Informatique</h4>
            <div style={{ fontSize: '0.82rem', color: '#888' }}>
              Inventaire appareils IT — téléphones, tablettes, laptops, imprimantes…
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading} style={{ borderRadius: 8 }}>
              <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Actualiser
            </Button>
            <Button size="sm" variant="outline-success" onClick={handleExport} style={{ borderRadius: 8 }}>
              <FaFileExcel /> Exporter
            </Button>
            <Button size="sm" variant="outline-dark" onClick={openQuick} style={{ borderRadius: 8 }}>
              <FaFilePdf /> Bon de remise
            </Button>
            {canWrite && (
              <>
                <label className="btn btn-sm btn-outline-primary" style={{ borderRadius: 8, margin: 0, cursor: 'pointer' }}>
                  {importing ? <Spinner size="sm" /> : <><FaUpload /> Importer Excel</>}
                  <input type="file" accept=".xlsx,.xls" onChange={handleImport} hidden />
                </label>
                <Button
                  size="sm"
                  style={{ background: MAIN, border: 'none', borderRadius: 8, fontWeight: 700 }}
                  onClick={openCreate}
                >
                  <FaPlus /> Ajouter
                </Button>
              </>
            )}
          </div>
        </div>

        {/* KPI statuts */}
        <div className="d-flex gap-2 flex-wrap mb-3">
          <KpiCard label="Total" value={rows.length} color={MAIN}
            active={kpi === ''} onClick={() => setKpi('')} />
          {STATUTS.map(s => (
            <KpiCard key={s} label={s} value={byStatut[s] || 0} color={STATUT_COLORS[s]}
              active={kpi === s} onClick={() => setKpi(f => f === s ? '' : s)} />
          ))}
        </div>

        {/* Chips type */}
        <div className="mb-3">
          {TYPES.filter(t => byType[t] > 0).map(t => (
            <TypeChip key={t} icon={TYPE_ICONS[t]} label={t} count={byType[t]}
              active={fType === t} onClick={() => setFType(f => f === t ? '' : t)} />
          ))}
        </div>

        {/* Filtres */}
        <Row className="g-2 mb-3">
          <Col xs={12} sm={6} md={3}>
            <InputGroup size="sm">
              <InputGroup.Text style={{ background: '#f5ede8', border: '1.5px solid #d8c8b8' }}>
                <FaSearch style={{ color: MAIN }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Marque, modèle, série, personne…"
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ border: '1.5px solid #d8c8b8', borderLeft: 'none', borderRadius: '0 8px 8px 0' }}
              />
            </InputGroup>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Select size="sm" value={fStatut} onChange={e => setFStatut(e.target.value)}
              style={{ border: '1.5px solid #d8c8b8', borderRadius: 8 }}>
              <option value="">Tous statuts</option>
              {STATUTS.map(s => <option key={s}>{s}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Select size="sm" value={fEtat} onChange={e => setFEtat(e.target.value)}
              style={{ border: '1.5px solid #d8c8b8', borderRadius: 8 }}>
              <option value="">Tous états</option>
              {ETATS.map(e => <option key={e}>{e}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <Form.Select size="sm" value={fSociete} onChange={e => setFSociete(e.target.value)}
              style={{ border: '1.5px solid #d8c8b8', borderRadius: 8 }}>
              <option value="">Toutes sociétés</option>
              {societes.map(s => <option key={s}>{s}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} sm={3} md={2}>
            <div style={{ fontSize: '0.8rem', color: '#888', paddingTop: 6 }}>
              <FaFilter style={{ color: MAIN }} /> {filtered.length} appareil{filtered.length > 1 ? 's' : ''}
            </div>
          </Col>
        </Row>

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: MAIN }} />
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 12px #0000001a' }}>
            <table className="pit">
              <thead>
                <tr>
                  <Th k="type_appareil" label="Type" />
                  <Th k="marque" label="Marque / Modèle" />
                  <th className="pit-th">N° Série / IMEI</th>
                  <Th k="etat" label="État" />
                  <Th k="statut" label="Statut" />
                  <Th k="affecte_a" label="Affecté à" />
                  <Th k="societe" label="Société" />
                  <Th k="date_affectation" label="Date affect." />
                  <th className="pit-th">Ligne tél</th>
                  <Th k="prix_achat" label="Prix achat" />
                  <th className="pit-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="pit-td text-center text-muted py-4">
                      Aucun appareil trouvé.
                    </td>
                  </tr>
                ) : filtered.map(r => (
                  <tr key={r.id} className="pit-tr">
                    <td className="pit-td">
                      <span style={{ color: MAIN, fontSize: '1rem', marginRight: 5 }}>
                        {TYPE_ICONS[r.type_appareil] || <FaCog />}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#666' }}>{r.type_appareil || '—'}</span>
                    </td>
                    <td className="pit-td">
                      <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{r.marque || '—'}</div>
                      <div style={{ color: '#888', fontSize: '0.75rem' }}>{r.modele || ''}</div>
                    </td>
                    <td className="pit-td">
                      <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#555' }}>
                        {r.numero_serie || '—'}
                      </div>
                      {r.imei && (
                        <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#888' }}>
                          IMEI: {r.imei}
                        </div>
                      )}
                    </td>
                    <td className="pit-td"><EtatBadge etat={r.etat} /></td>
                    <td className="pit-td"><StatutBadge statut={r.statut} /></td>
                    <td className="pit-td" style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      {r.affecte_a || '—'}
                    </td>
                    <td className="pit-td"><SocBadge societe={r.societe} /></td>
                    <td className="pit-td" style={{ fontSize: '0.78rem', color: '#666' }}>
                      {fmtDate(r.date_affectation)}
                      {r.date_retour_prevu && (
                        <div style={{ color: '#e67e22', fontSize: '0.72rem' }}>
                          ↩ {fmtDate(r.date_retour_prevu)}
                        </div>
                      )}
                    </td>
                    <td className="pit-td">
                      {r.ligne ? (
                        <span style={{ fontSize: '0.78rem', color: MAIN, fontWeight: 600 }}>
                          📱 {r.ligne.tel}
                          {r.ligne.operateur && (
                            <span style={{ color: '#888', fontWeight: 400 }}> · {r.ligne.operateur}</span>
                          )}
                        </span>
                      ) : <span style={{ color: '#ddd' }}>—</span>}
                    </td>
                    <td className="pit-td" style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                      {fmtPrice(r.prix_achat)}
                    </td>
                    <td className="pit-td" style={{ whiteSpace: 'nowrap' }}>
                      <button className="pit-btn" title="Historique" onClick={() => openHist(r)}
                        style={{ color: '#1565c0' }}><FaHistory /></button>
                      {r.statut === 'Affecté' && (
                        <button className="pit-btn" title="Bon de remise (PDF)" onClick={() => openBonRemise(r)}
                          style={{ color: '#6d4e36' }}><FaFilePdf /></button>
                      )}
                      {canWrite && (
                        <>
                          <button className="pit-btn" title="Modifier" onClick={() => openEdit(r)}
                            style={{ color: MAIN }}><FaEdit /></button>
                          {r.statut !== 'Affecté' ? (
                            <button className="pit-btn" title="Affecter" onClick={() => openAff(r)}
                              style={{ color: '#2e7d32' }}><FaUserCheck /></button>
                          ) : (
                            <button className="pit-btn" title="Restituer" onClick={() => openRest(r)}
                              style={{ color: '#e67e22' }}><FaUserTimes /></button>
                          )}
                          <button className="pit-btn" title="Supprimer" onClick={() => setDelTarget(r)}
                            style={{ color: '#c0392b' }}><FaTrash /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ══ Modal Add/Edit ══════════════════════════════════════ */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header style={{ background: MAIN, color: '#fff' }} closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
              {editRow ? '✏️ Modifier l\'appareil' : '➕ Nouvel appareil IT'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formErr && <Alert variant="danger" className="py-2 small">{formErr}</Alert>}
            <Row className="g-2">
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Type d'appareil
                </label>
                <Form.Select value={form.type_appareil}
                  onChange={e => setForm(f => ({ ...f, type_appareil: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Choisir —</option>
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </Form.Select>
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Marque</label>
                <Form.Control value={form.marque}
                  onChange={e => setForm(f => ({ ...f, marque: e.target.value }))}
                  placeholder="Apple, Samsung, HP…" className="pit-inp" />
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Modèle</label>
                <Form.Control value={form.modele}
                  onChange={e => setForm(f => ({ ...f, modele: e.target.value }))}
                  placeholder="iPhone 15, ThinkPad X1…" className="pit-inp" />
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Numéro de série</label>
                <Form.Control value={form.numero_serie}
                  onChange={e => setForm(f => ({ ...f, numero_serie: e.target.value }))}
                  placeholder="SXXXXXXXXX" className="pit-inp" />
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>IMEI (téléphones)</label>
                <Form.Control value={form.imei}
                  onChange={e => setForm(f => ({ ...f, imei: e.target.value }))}
                  placeholder="15 chiffres" className="pit-inp" />
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>État</label>
                <Form.Select value={form.etat}
                  onChange={e => setForm(f => ({ ...f, etat: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Choisir —</option>
                  {ETATS.map(e => <option key={e}>{e}</option>)}
                </Form.Select>
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Statut</label>
                <Form.Select value={form.statut}
                  onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}
                  className="pit-inp">
                  {STATUTS.map(s => <option key={s}>{s}</option>)}
                </Form.Select>
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Société</label>
                <Form.Select value={form.societe}
                  onChange={e => setForm(f => ({ ...f, societe: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Choisir —</option>
                  {societes.map(s => <option key={s}>{s}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Affecté à</label>
                <Form.Control value={form.affecte_a}
                  onChange={e => setForm(f => ({ ...f, affecte_a: e.target.value }))}
                  placeholder="Nom et prénom" className="pit-inp" />
              </Col>
              <Col md={3}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Date affectation</label>
                <Form.Control type="date" value={form.date_affectation}
                  onChange={e => setForm(f => ({ ...f, date_affectation: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col md={3}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Retour prévu</label>
                <Form.Control type="date" value={form.date_retour_prevu}
                  onChange={e => setForm(f => ({ ...f, date_retour_prevu: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Ligne téléphonique
                </label>
                <Form.Select value={form.telephone_id}
                  onChange={e => setForm(f => ({ ...f, telephone_id: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Aucune —</option>
                  {telephones.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.tel}{t.nom_prenom ? ` · ${t.nom_prenom}` : ''}{t.operateur ? ` (${t.operateur})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Prix achat (DH)</label>
                <Form.Control type="number" step="0.01" value={form.prix_achat}
                  onChange={e => setForm(f => ({ ...f, prix_achat: e.target.value }))}
                  placeholder="0.00" className="pit-inp" />
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Fournisseur</label>
                <Form.Control value={form.fournisseur}
                  onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))}
                  placeholder="Nom fournisseur" className="pit-inp" />
              </Col>
              <Col md={4}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Date achat</label>
                <Form.Control type="date" value={form.date_achat}
                  onChange={e => setForm(f => ({ ...f, date_achat: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col md={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Observation</label>
                <Form.Control as="textarea" rows={2} value={form.observation}
                  onChange={e => setForm(f => ({ ...f, observation: e.target.value }))}
                  className="pit-inp" />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer style={{ background: '#fdfaf8' }}>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button size="sm" disabled={saving}
              style={{ background: MAIN, border: 'none', fontWeight: 700 }}
              onClick={handleSave}>
              {saving ? <Spinner size="sm" /> : (editRow ? 'Enregistrer' : 'Créer')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Modal Historique ════════════════════════════════════ */}
        <Modal show={showHist} onHide={() => setShowHist(false)} size="lg" centered>
          <Modal.Header style={{ background: '#1565c0', color: '#fff' }} closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
              📋 Historique — {histAppareil?.marque} {histAppareil?.modele}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {histAppareil && (
              <div className="d-flex gap-3 flex-wrap mb-3 p-2"
                style={{ background: '#f5f5f5', borderRadius: 8, fontSize: '0.85rem' }}>
                <span style={{ color: MAIN, fontSize: '1.1rem' }}>
                  {TYPE_ICONS[histAppareil.type_appareil] || <FaCog />}
                </span>
                <strong>{histAppareil.type_appareil}</strong>
                <span>S/N : <strong>{histAppareil.numero_serie || '—'}</strong></span>
                {histAppareil.imei && <span>IMEI : <strong>{histAppareil.imei}</strong></span>}
                <EtatBadge etat={histAppareil.etat} />
                <StatutBadge statut={histAppareil.statut} />
              </div>
            )}
            {loadHist ? (
              <div className="text-center py-3"><Spinner animation="border" /></div>
            ) : histRows.length === 0 ? (
              <div className="text-center text-muted py-3">Aucune affectation enregistrée.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#1565c020' }}>
                    {['Affecté à', 'Date affectation', 'Date retour', 'Motif', 'Note'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', fontWeight: 700, color: '#1565c0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {histRows.map((h, i) => (
                    <tr key={h.id} style={{ background: i % 2 === 0 ? '#fff' : '#f7fbff', borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '7px 12px', fontWeight: 600 }}>{h.affecte_a || '—'}</td>
                      <td style={{ padding: '7px 12px' }}>{fmtDate(h.date_affectation)}</td>
                      <td style={{ padding: '7px 12px' }}>
                        {h.date_retour
                          ? fmtDate(h.date_retour)
                          : <Badge bg="primary" style={{ fontSize: '0.7rem' }}>En cours</Badge>}
                      </td>
                      <td style={{ padding: '7px 12px', color: '#666' }}>{h.motif_retour || '—'}</td>
                      <td style={{ padding: '7px 12px', color: '#888', fontStyle: 'italic' }}>{h.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowHist(false)}>Fermer</Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Modal Affecter ══════════════════════════════════════ */}
        <Modal show={showAff} onHide={() => setShowAff(false)} centered>
          <Modal.Header style={{ background: '#2e7d32', color: '#fff' }} closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
              ✅ Affecter — {affTarget?.marque} {affTarget?.modele}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {affErr && <Alert variant="danger" className="py-2 small">{affErr}</Alert>}
            <Row className="g-2">
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Affecté à *
                </label>
                <Form.Control value={affForm.affecte_a}
                  onChange={e => setAffForm(f => ({ ...f, affecte_a: e.target.value }))}
                  placeholder="Nom et prénom" className="pit-inp" />
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Date affectation</label>
                <Form.Control type="date" value={affForm.date_affectation}
                  onChange={e => setAffForm(f => ({ ...f, date_affectation: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Retour prévu</label>
                <Form.Control type="date" value={affForm.date_retour_prevu}
                  onChange={e => setAffForm(f => ({ ...f, date_retour_prevu: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Accessoires fournis
                </label>
                <Form.Control as="textarea" rows={2} value={affForm.accessoires}
                  onChange={e => setAffForm(f => ({ ...f, accessoires: e.target.value }))}
                  placeholder="Chargeur, housse, écouteurs…" className="pit-inp" />
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Note</label>
                <Form.Control as="textarea" rows={2} value={affForm.note}
                  onChange={e => setAffForm(f => ({ ...f, note: e.target.value }))}
                  className="pit-inp" />
              </Col>
            </Row>
            <div className="small text-muted mt-1">
              <FaFilePdf /> Un bon de remise (PDF) sera téléchargé automatiquement à la confirmation.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowAff(false)}>Annuler</Button>
            <Button size="sm" disabled={affSaving}
              style={{ background: '#2e7d32', border: 'none', fontWeight: 700 }}
              onClick={handleAff}>
              {affSaving ? <Spinner size="sm" /> : 'Affecter'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Modal Restituer ═════════════════════════════════════ */}
        <Modal show={showRest} onHide={() => setShowRest(false)} centered>
          <Modal.Header style={{ background: '#e67e22', color: '#fff' }} closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
              🔄 Restituer — {restTarget?.marque} {restTarget?.modele}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {restErr && <Alert variant="danger" className="py-2 small">{restErr}</Alert>}
            <p className="small text-muted mb-2">
              Actuellement affecté à : <strong>{restTarget?.affecte_a || '—'}</strong>
            </p>
            <Row className="g-2">
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Date retour</label>
                <Form.Control type="date" value={restForm.date_retour}
                  onChange={e => setRestForm(f => ({ ...f, date_retour: e.target.value }))}
                  className="pit-inp" />
              </Col>
              <Col md={6}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Nouveau statut</label>
                <Form.Select value={restForm.nouveau_statut}
                  onChange={e => setRestForm(f => ({ ...f, nouveau_statut: e.target.value }))}
                  className="pit-inp">
                  <option>Disponible</option>
                  <option>En réparation</option>
                  <option>Hors service</option>
                </Form.Select>
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Motif de retour</label>
                <Form.Control value={restForm.motif_retour}
                  onChange={e => setRestForm(f => ({ ...f, motif_retour: e.target.value }))}
                  placeholder="Fin de contrat, mutation, panne…" className="pit-inp" />
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>Note</label>
                <Form.Control as="textarea" rows={2} value={restForm.note}
                  onChange={e => setRestForm(f => ({ ...f, note: e.target.value }))}
                  className="pit-inp" />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowRest(false)}>Annuler</Button>
            <Button size="sm" disabled={restSaving}
              style={{ background: '#e67e22', border: 'none', fontWeight: 700 }}
              onClick={handleRest}>
              {restSaving ? <Spinner size="sm" /> : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Modal Bon de remise rapide ══════════════════════════ */}
        <Modal show={showQuick} onHide={() => setShowQuick(false)} centered>
          <Modal.Header style={{ background: MAIN, color: '#fff' }} closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800 }}>
              <FaFilePdf /> Bon de remise
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {quickErr && <Alert variant="danger" className="py-2 small">{quickErr}</Alert>}
            <Row className="g-2">
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Article *
                </label>
                <Form.Select value={quickForm.parc_id}
                  onChange={e => setQuickForm(f => ({ ...f, parc_id: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Choisir un article —</option>
                  {rows.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.type_appareil} · {r.marque} {r.modele}{r.numero_serie ? ` (S/N: ${r.numero_serie})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Bénéficiaire
                </label>
                <Form.Control value={quickForm.affecte_a}
                  onChange={e => setQuickForm(f => ({ ...f, affecte_a: e.target.value }))}
                  placeholder="Nom et prénom (facultatif)" className="pit-inp" />
              </Col>
              <Col xs={12}>
                <label className="d-block" style={{ fontSize: '.85rem', fontWeight: 700, color: '#555', marginBottom: 3 }}>
                  Société
                </label>
                <Form.Select value={quickForm.societe}
                  onChange={e => setQuickForm(f => ({ ...f, societe: e.target.value }))}
                  className="pit-inp">
                  <option value="">— Choisir —</option>
                  {societes.map(s => <option key={s}>{s}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowQuick(false)}>Annuler</Button>
            <Button size="sm" style={{ background: MAIN, border: 'none', fontWeight: 700 }} onClick={handleQuick}>
              <FaFilePdf /> Générer le PDF
            </Button>
          </Modal.Footer>
        </Modal>

        {/* ══ Modal Suppression ═══════════════════════════════════ */}
        <Modal show={!!delTarget} onHide={() => setDelTarget(null)} centered size="sm">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1rem', fontWeight: 800, color: '#c0392b' }}>
              🗑️ Supprimer ?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: '0.9rem' }}>
              Supprimer <strong>{delTarget?.marque} {delTarget?.modele}</strong>
              {delTarget?.numero_serie ? ` (S/N: ${delTarget.numero_serie})` : ''} ?
            </p>
            <p className="text-danger small">Tout l'historique d'affectation sera supprimé.</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setDelTarget(null)}>Annuler</Button>
            <Button variant="danger" size="sm" onClick={handleDelete}>Supprimer</Button>
          </Modal.Footer>
        </Modal>

      </div>
    </>
  );
}
